import type { SectionKey } from '#/services/settings/spec.js'
import { sectionByKey } from '#/services/settings/spec.js'
import { clear, el } from './dom.js'
import { sectionHelp } from './help.js'
import { closeIcon, sectionIcon } from './icons.js'
import { t } from './labels.js'

// The per-section guide — a bottom sheet (DOM transport; live-verify only). openHelp(key) builds a modal
// dialog explaining one section and every parameter in it (content from help.ts; titles from the shared
// labels, so wording can't drift). It is dismissed by the close button, a backdrop tap, Escape, OR
// Telegram's BackButton (main.ts routes Back through closeHelp() first). Only one sheet exists at a time.
//
// Accessibility: role="dialog" + aria-modal, labelled by the section title; focus moves into the sheet on
// open and is restored to the trigger on close; Tab is trapped inside; body scroll is locked while open.
// Motion (slide-up + backdrop fade) is pure CSS and reduced-motion-gated there; here we only toggle .open
// and remove the node after the transition (with a timeout fallback, so a missed transitionend can't leak
// a detached, scroll-locking overlay). Touches `document` only when called → safe to import in Node tests.

let backdrop: HTMLElement | null = null
let panel: HTMLElement | null = null
let trigger: HTMLElement | null = null
let onKeydown: ((e: KeyboardEvent) => void) | null = null
let onCloseCb: (() => void) | null = null // the open sheet's close hook (see openSheet) — fired ONCE per close
let sheetSeq = 0 // monotonic — gives each open a UNIQUE title id, so an overlapping close→reopen can't
// briefly produce two elements sharing id="sheet-title" (which would point aria-labelledby at the wrong one).

const CLOSE_MS = 360 // > --dur-view (320ms); the fallback that guarantees teardown if transitionend is missed

function teardown(node: HTMLElement): void {
  // Idempotent: remove() on an already-detached node is a no-op, so the transitionend handler and the
  // timeout fallback can both fire teardown on the same node without harm.
  node.remove()
}

/** Close the open guide, if any. Returns true if one was open (so the BackButton handler can stop here). */
export function closeHelp(): boolean {
  if (!backdrop)
    return false
  const closing = backdrop
  backdrop = null
  panel = null
  // The close hook fires exactly once, whatever the close path (×, backdrop, Escape, Telegram Back, or a
  // replacing openSheet). Module state is cleared FIRST so a re-entrant closeHelp from inside the hook is
  // a no-op; it is INVOKED below, after the teardown is scheduled (the editor commits its text there).
  const closeHook = onCloseCb
  onCloseCb = null
  if (onKeydown) {
    document.removeEventListener('keydown', onKeydown)
    onKeydown = null
  }
  document.body.style.removeProperty('overflow')
  closing.classList.remove('open')
  // Teardown after the SHEET's slide-out (transform, --dur-view) completes — NOT the backdrop's shorter
  // opacity fade, whose transitionend bubbles up first and would cut the animation ~80ms short. The 360ms
  // timeout is the fallback if no transitionend arrives (e.g. reduced-motion zeroes the duration).
  const onEnd = (e: TransitionEvent): void => {
    if (e.propertyName !== 'transform')
      return
    closing.removeEventListener('transitionend', onEnd)
    teardown(closing)
  }
  closing.addEventListener('transitionend', onEnd)
  window.setTimeout(() => teardown(closing), CLOSE_MS)
  // AFTER the teardown is scheduled, so a hook that throws can never strand the overlay on screen. The
  // sheet's nodes are still attached while the close animates, so the hook can read its live fields.
  closeHook?.()
  trigger?.focus()
  trigger = null
  return true
}

/** Focusable elements inside the panel (for the Tab trap). Text fields joined the list with the editor sheet. */
function focusables(): HTMLElement[] {
  if (!panel)
    return []
  return [...panel.querySelectorAll<HTMLElement>('button, [href], textarea, input, select, [tabindex]:not([tabindex="-1"])')]
}

function trapTab(e: KeyboardEvent): void {
  const items = focusables()
  if (items.length === 0)
    return
  const first = items[0]!
  const last = items[items.length - 1]!
  const active = document.activeElement
  if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  }
  else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

// Render guide prose, turning backtick-delimited value tokens into .param-val tokens (e.g. `mute`,
// `5s`, `delete`). Splitting on the backtick yields alternating text / value segments (even index = plain
// text, odd = a value); each part is a text node or an el()-built span, so this stays XSS-safe even though
// the prose is static/trusted. Balanced markers are guarded by help.test.ts (an odd count would mis-colour).
function renderProse(text: string): (string | HTMLElement)[] {
  return text.split('`').map((seg, i) => (i % 2 === 1 ? el('span', { class: 'param-val' }, seg) : seg))
}

/**
 * Every sheet must SAY WHAT IT IS. `bodyKey` is a label key explaining the sheet in plain language — the
 * head then carries a "?" that expands it INLINE (see below). `'self'` is the deliberate, visible
 * exemption for a sheet that is ITSELF the explanation (the section guide, the lite info sheet): the
 * point is that skipping the guide must be a decision someone typed, not something quietly forgotten.
 *
 * This is a TYPE-level rule, not a convention: a new sheet cannot compile without answering the question.
 * Operator's standing rule (2026-07-30): a feature nobody can find or understand is a feature not
 * delivered — `trusted` sat unused for three weeks for exactly this reason.
 */
export type SheetHelp = { bodyKey: string } | 'self'

/**
 * The "?" for a CONTENT sheet expands its explanation INLINE, under the head — it must NOT open another
 * sheet: openSheet tears the current one down first (one sheet at a time), so an info sheet launched from
 * the drill-down would close the very thing it explains and fire its onClose, unwinding the navigation.
 * Collapsed by default (the explanation is for whoever wants it; it must not push the content down every
 * time), toggled by the same button, with aria-expanded kept truthful.
 */
function sheetHelpPanel(bodyKey: string): { button: HTMLElement, panel: HTMLElement } {
  const panel = el('p', { class: 'sheet-help', hidden: true }, t(bodyKey))
  const button = el('button', { 'class': 'help-btn', 'type': 'button', 'aria-expanded': 'false', 'aria-label': t('ui-about') }, '?')
  button.addEventListener('click', () => {
    const open = panel.hasAttribute('hidden')
    panel.toggleAttribute('hidden', !open)
    button.setAttribute('aria-expanded', String(open))
  })
  return { button, panel }
}

// Build the dialog SHELL (the role="dialog" panel: a head with an optional icon + title + the "?"
// explanation toggle + close button, then the caller's body nodes) and mount it as a bottom-sheet —
// wiring the backdrop tap, Escape + Tab trap, body-scroll lock, slide-up animation, and initial focus.
// The one place every sheet variant shares, so the section guide, the lite info sheet AND the bespoke
// content sheets (the AI-Shield drill-down, which keeps mutating its body nodes after open — the Tab trap
// picks dynamic buttons/links up because focusables() queries live) can't drift on a11y, teardown OR the
// duty to explain themselves. `title` is resolved text. Exported for the content-sheet callers; one sheet
// exists at a time and Telegram's Back closes it via closeHelp().
// `onClose` (optional) fires exactly once when the sheet closes, on EVERY close path — the text-editor
// sheet commits a pending edit there, so no dismissal gesture can silently drop typed text.
export function openSheet(icon: SVGSVGElement | null, title: string, body: (HTMLElement | string)[], help: SheetHelp, triggerEl?: HTMLElement, onClose?: () => void): void {
  closeHelp() // never stack; tear any existing one down first (fires ITS onClose before ours is armed)
  trigger = triggerEl ?? null
  onCloseCb = onClose ?? null

  const titleId = `sheet-title-${++sheetSeq}`
  const dialog = el('div', { 'class': 'sheet', 'role': 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId, 'tabindex': '-1' })

  const head = el('div', { class: 'sheet-head' })
  if (icon)
    head.append(icon)
  head.append(el('h2', { class: 'sheet-title', id: titleId }, title))
  // The explanation toggle sits before the ×, so the reflex "tap the last button" stays "close".
  const explain = help === 'self' ? null : sheetHelpPanel(help.bodyKey)
  if (explain)
    head.append(explain.button)
  const close = el('button', { 'class': 'sheet-close', 'type': 'button', 'aria-label': t('ui-help-close') }, closeIcon('icon-sm'))
  close.addEventListener('click', () => closeHelp())
  head.append(close)
  dialog.append(head)
  if (explain)
    dialog.append(explain.panel)
  for (const node of body)
    dialog.append(node)

  const back = el('div', { class: 'sheet-backdrop' }, dialog)
  back.addEventListener('click', (e) => {
    if (e.target === back) // a tap on the dim area, not inside the panel
      closeHelp()
  })

  onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault() // used: the error banner's own Escape (dom.ts) waits for the next press
      closeHelp()
    }
    else if (e.key === 'Tab') {
      trapTab(e)
    }
  }
  document.addEventListener('keydown', onKeydown)

  document.body.style.overflow = 'hidden'
  document.body.append(back)
  backdrop = back
  panel = dialog

  // Insert without .open, then add it on a later frame so the CSS transition actually plays (a class set
  // in the same paint as insertion would skip the animation). Double rAF is the robust cross-engine way.
  // Guard on `backdrop === back`: if a rapid close/reopen swapped the sheet meanwhile, don't animate a
  // node that's already being torn down (the replacement schedules its own .open).
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (backdrop === back)
      back.classList.add('open')
  }))
  close.focus()
}

/** Open the per-section guide. `triggerEl` (the tapped help button) regains focus on close. */
export function openHelp(key: SectionKey, triggerEl?: HTMLElement): void {
  const section = sectionByKey(key)
  if (!section)
    return
  const help = sectionHelp(key)

  const body: (HTMLElement | string)[] = [
    el('p', { class: 'sheet-body' }, ...renderProse(help.body)),
    el('h3', { class: 'sheet-sub' }, t('ui-help-params-title')),
  ]
  // One term/description pair per spec field, in the SAME order as the form — names from labels (no drift).
  const list = el('dl', { class: 'param-list' })
  for (const field of section.fields) {
    list.append(el('dt', { class: 'param-name' }, t(field.labelKey)))
    list.append(el('dd', { class: 'param-desc' }, ...renderProse(help.params[field.field] ?? '')))
  }
  body.push(list)

  openSheet(sectionIcon(key, 'sheet-icon'), t(section.titleKey), body, 'self', triggerEl)
}

/**
 * Open a LITE guide sheet — a head (icon + title) and a single prose paragraph, NO parameter list. For a
 * one-off explanation that isn't a settings section (e.g. the General-panel silent-mode toggle). `titleKey` /
 * `bodyKey` resolve through the shared labels; the body is plain text (no `value` markup like the sections).
 */
export function openInfo(titleKey: string, bodyKey: string, icon: SVGSVGElement | null, triggerEl?: HTMLElement): void {
  openSheet(icon, t(titleKey), [el('p', { class: 'sheet-body' }, t(bodyKey))], 'self', triggerEl)
}

/**
 * The bold "?" trigger that opens a LITE info sheet (openInfo) — the ONE definition of that button, shared
 * by every bespoke section head via sectionHead below (cold-eye fold: it was copy-pasted). The a11y
 * contract lives here once: class `help-btn`, an aria-label `"<About> <title>"` (so the "?" glyph is
 * purely visual), and the click wiring that restores focus to this button on close. `icon` is a FACTORY
 * (generalIcon / statsIcon / …), invoked per-open so each sheet gets its own fresh SVG node.
 */
export function infoHelpButton(titleKey: string, bodyKey: string, icon: (className: string) => SVGSVGElement): HTMLElement {
  const btn = el('button', { 'class': 'help-btn', 'type': 'button', 'aria-label': `${t('ui-about')} ${t(titleKey)}` }, '?')
  btn.addEventListener('click', () => openInfo(titleKey, bodyKey, icon('sheet-icon'), btn))
  return btn
}

/**
 * A section head — the icon + prominent title + the "?" lite-info-sheet trigger — shared by the
 * read-only / bespoke sections (stats, log channel, AI-Shield activity, General). ONE definition of that
 * DOM + a11y skeleton so they can't drift (cold-eye fold). `icon` is the factory (statsIcon /
 * logChannelIcon / …), invoked for the head glyph AND (inside infoHelpButton) the sheet glyph. `bodyKey`
 * is REQUIRED since v8: every panel's head carries the same "?" trigger (user call, session 64 — General
 * was the odd one out with a row-level "?"). The spec-driven FORM sections have their OWN head (a per-key
 * sectionIcon + a "?" that opens the full openHelp sheet), so they deliberately don't use this.
 */
export function sectionHead(icon: (className: string) => SVGSVGElement, titleKey: string, bodyKey: string): HTMLElement {
  const head = el('div', { class: 'sec-head' }, icon('sec-icon'), el('h2', { class: 'sec-title' }, t(titleKey)))
  head.append(infoHelpButton(titleKey, bodyKey, icon))
  return head
}

/**
 * Render a read-only section's loud in-section error state into `root`: the section head, then a single
 * error row. Shared by the read-only sections (stats, AI-Shield activity — cold-eye fold) so a fix to the
 * error skeleton (e.g. wrapping the row in aria-live) lands in ONE place, not per section. `head` is the
 * section's already-built head; `errorKey` its message. The `.stats-error` class is the shared error-text
 * style (kept its historical name — both sections use it).
 */
export function renderSectionError(root: HTMLElement, head: HTMLElement, errorKey: string): void {
  clear(root)
  root.append(head)
  root.append(el('div', { class: 'sec-group' }, el('div', { class: 'row' }, el('span', { class: 'label stats-error' }, t(errorKey)))))
}
